import { useEffect, useLayoutEffect, useRef, useState } from "react";

// An unsent composer draft's home between mounts — leaving the chat tab for
// another, closing and reopening a side thread, or reloading the page. Keyed
// per task/thread so two composers never see each other's typing, and split
// into a text key and an attachments key (`chatDraftAttachmentsKey`) so the
// two states — one owned by the composer, the other by
// `useComposerAttachments` — never race each other writing the same entry.

export const chatDraftTextKey = (taskId: string): string =>
  `tin.chatDraft.${taskId}`;

export const chatDraftAttachmentsKey = (taskId: string): string =>
  `tin.chatDraft.${taskId}.attachments`;

// A side thread's key is its id once the server has minted one, and its
// anchor's id before that — the one thing that identifies a not-yet-created
// draft thread across a close/reopen of the panel.
export const sideThreadDraftTextKey = (
  taskId: string,
  threadOrAnchorId: string,
): string => `tin.chatDraft.side.${taskId}.${threadOrAnchorId}`;

export const sideThreadDraftAttachmentsKey = (
  taskId: string,
  threadOrAnchorId: string,
): string => `${sideThreadDraftTextKey(taskId, threadOrAnchorId)}.attachments`;

const loadDraftText = (key: string): string => {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
};

const saveDraftText = (key: string, text: string): void => {
  try {
    if (text === "") {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, text);
    }
  } catch {
    // Storage disabled or full (private browsing, quota) — typing itself is
    // unaffected, it just won't survive this mount.
  }
};

/**
 * A composer's draft text, persisted under `key` and restored on mount —
 * and re-restored, rather than left as the previous key's leftover text,
 * whenever `key` itself changes under an already-mounted composer (switching
 * tasks without leaving the chat tab; switching which thread a still-open
 * side-thread panel shows).
 */
export function usePersistedDraftText(
  key: string,
): [string, (text: string) => void] {
  const [text, setText] = useState(() => loadDraftText(key));
  const loadedKeyRef = useRef(key);

  useLayoutEffect(() => {
    if (loadedKeyRef.current === key) {
      return;
    }
    loadedKeyRef.current = key;
    setText(loadDraftText(key));
  }, [key]);

  useEffect(() => {
    saveDraftText(key, text);
  }, [key, text]);

  return [text, setText];
}
